import axiosInstance from "../Axios/axios";
import { inventoryConstants } from "../Constants/inventoryConstant";

const setLoading = (dispatch, isLoading) =>
  dispatch({ type: inventoryConstants.LOADING, isLoading });

const setResponse = (dispatch, state, message) =>
  dispatch({
    type: inventoryConstants.RESPONSE,
    response: { state, message },
  });

const errMessage = (e) =>
  e?.response?.data?.message ?? "Connection failed. Try again later";

// ── Items ──────────────────────────────────────────────────────────────
export const GetInventoryItems = (query = {}) => {
  return async (dispatch) => {
    try {
      setLoading(dispatch, true);
      const params = new URLSearchParams();
      if (query.low_stock) params.append("low_stock", query.low_stock);
      if (query.supplier) params.append("supplier", query.supplier);
      if (query.search) params.append("search", query.search);
      const response = await axiosInstance.get(
        `/inventory/items?${params.toString()}`
      );
      if (response) {
        dispatch({
          type: inventoryConstants.GET_ITEMS,
          payload: { items: response.data },
        });
        setLoading(dispatch, false);
      }
    } catch (e) {
      setResponse(dispatch, "ERROR", errMessage(e));
      setLoading(dispatch, false);
    }
  };
};

export const GetInventoryItemById = (id) => {
  return async (dispatch) => {
    try {
      setLoading(dispatch, true);
      const response = await axiosInstance.get(`/inventory/items/${id}`);
      if (response) {
        dispatch({
          type: inventoryConstants.GET_ITEM_DETAILED,
          payload: { item: response.data },
        });
        setLoading(dispatch, false);
      }
    } catch (e) {
      setResponse(dispatch, "ERROR", errMessage(e));
      setLoading(dispatch, false);
    }
  };
};

export const CreateInventoryItem = (data) => {
  return async (dispatch) => {
    try {
      setLoading(dispatch, true);
      const res = await axiosInstance.post(`/inventory/items`, data);
      if (res) {
        setResponse(dispatch, "SUCCESS", "Inventory item created");
        setLoading(dispatch, false);
      }
    } catch (e) {
      setResponse(dispatch, "ERROR", errMessage(e));
      setLoading(dispatch, false);
    }
  };
};

export const UpdateInventoryItem = (id, data) => {
  return async (dispatch) => {
    try {
      setLoading(dispatch, true);
      const res = await axiosInstance.put(`/inventory/items/${id}`, data);
      if (res) {
        setResponse(dispatch, "SUCCESS", "Inventory item updated");
        setLoading(dispatch, false);
      }
    } catch (e) {
      setResponse(dispatch, "ERROR", errMessage(e));
      setLoading(dispatch, false);
    }
  };
};

export const AdjustStock = (id, data) => {
  return async (dispatch) => {
    try {
      setLoading(dispatch, true);
      const res = await axiosInstance.post(
        `/inventory/items/${id}/adjust`,
        data
      );
      if (res) {
        setResponse(dispatch, "SUCCESS", "Stock adjusted");
        setLoading(dispatch, false);
      }
    } catch (e) {
      setResponse(dispatch, "ERROR", errMessage(e));
      setLoading(dispatch, false);
    }
  };
};

export const ImportFromProducts = () => {
  return async (dispatch) => {
    try {
      setLoading(dispatch, true);
      const res = await axiosInstance.post(`/inventory/import`);
      if (res) {
        const { created = 0, skipped = 0 } = res.data || {};
        setResponse(
          dispatch,
          "SUCCESS",
          `Backfill complete: ${created} created, ${skipped} already existed`
        );
        setLoading(dispatch, false);
      }
    } catch (e) {
      setResponse(dispatch, "ERROR", errMessage(e));
      setLoading(dispatch, false);
    }
  };
};

// ── Scanner ────────────────────────────────────────────────────────────
export const ScanItem = (data) => {
  return async (dispatch) => {
    try {
      setLoading(dispatch, true);
      const res = await axiosInstance.post(`/inventory/scan`, data);
      if (res) {
        dispatch({
          type: inventoryConstants.SCAN_RESULT,
          payload: { scan_result: res.data },
        });
        const verb =
          data?.mode === "RECEIVE"
            ? "received"
            : data?.mode === "DISPATCH"
            ? "dispatched"
            : "counted";
        setResponse(dispatch, "SUCCESS", `Scan ${verb} successfully`);
        setLoading(dispatch, false);
      }
    } catch (e) {
      setResponse(dispatch, "ERROR", errMessage(e));
      setLoading(dispatch, false);
    }
  };
};

// ── Stock-take / verification ────────────────────────────────────────────
export const OpenStockTake = (data) => {
  return async (dispatch) => {
    try {
      setLoading(dispatch, true);
      const res = await axiosInstance.post(`/inventory/stock-take`, data);
      if (res) {
        setResponse(dispatch, "SUCCESS", "Stock-take session opened");
        setLoading(dispatch, false);
      }
    } catch (e) {
      setResponse(dispatch, "ERROR", errMessage(e));
      setLoading(dispatch, false);
    }
  };
};

export const GetStockTakes = () => {
  return async (dispatch) => {
    try {
      setLoading(dispatch, true);
      const response = await axiosInstance.get(`/inventory/stock-take`);
      if (response) {
        dispatch({
          type: inventoryConstants.GET_STOCK_TAKES,
          payload: { stock_takes: response.data },
        });
        setLoading(dispatch, false);
      }
    } catch (e) {
      setResponse(dispatch, "ERROR", errMessage(e));
      setLoading(dispatch, false);
    }
  };
};

export const GetStockTakeById = (id) => {
  return async (dispatch) => {
    try {
      setLoading(dispatch, true);
      const response = await axiosInstance.get(`/inventory/stock-take/${id}`);
      if (response) {
        dispatch({
          type: inventoryConstants.GET_STOCK_TAKE_DETAILED,
          payload: { stock_take: response.data },
        });
        setLoading(dispatch, false);
      }
    } catch (e) {
      setResponse(dispatch, "ERROR", errMessage(e));
      setLoading(dispatch, false);
    }
  };
};

export const RecordCount = (id, data) => {
  return async (dispatch) => {
    try {
      const res = await axiosInstance.post(
        `/inventory/stock-take/${id}/count`,
        data
      );
      if (res) {
        dispatch({
          type: inventoryConstants.GET_STOCK_TAKE_DETAILED,
          payload: { stock_take: res.data },
        });
      }
    } catch (e) {
      setResponse(dispatch, "ERROR", errMessage(e));
    }
  };
};

export const CloseStockTake = (id) => {
  return async (dispatch) => {
    try {
      setLoading(dispatch, true);
      const res = await axiosInstance.post(
        `/inventory/stock-take/${id}/close`
      );
      if (res) {
        setResponse(
          dispatch,
          "SUCCESS",
          `Stock-take closed. Total variance: ${res.data?.total_variance ?? 0}`
        );
        setLoading(dispatch, false);
      }
    } catch (e) {
      setResponse(dispatch, "ERROR", errMessage(e));
      setLoading(dispatch, false);
    }
  };
};

// ── Movements & reports ──────────────────────────────────────────────────
export const GetMovements = (query = {}) => {
  return async (dispatch) => {
    try {
      setLoading(dispatch, true);
      const params = new URLSearchParams();
      if (query.item) params.append("item", query.item);
      if (query.type) params.append("type", query.type);
      if (query.limit) params.append("limit", query.limit);
      const response = await axiosInstance.get(
        `/inventory/movements?${params.toString()}`
      );
      if (response) {
        dispatch({
          type: inventoryConstants.GET_MOVEMENTS,
          payload: { movements: response.data },
        });
        setLoading(dispatch, false);
      }
    } catch (e) {
      setResponse(dispatch, "ERROR", errMessage(e));
      setLoading(dispatch, false);
    }
  };
};

export const GetLowStock = () => {
  return async (dispatch) => {
    try {
      const response = await axiosInstance.get(`/inventory/reports/low-stock`);
      if (response) {
        dispatch({
          type: inventoryConstants.GET_LOW_STOCK,
          payload: { low_stock: response.data },
        });
      }
    } catch (e) {
      setResponse(dispatch, "ERROR", errMessage(e));
    }
  };
};

// Fetch the label PDF as a blob (so the auth token is sent) and open it.
export const PrintLabel = (id) => {
  return async (dispatch) => {
    try {
      const response = await axiosInstance.get(
        `/inventory/items/${id}/label.pdf`,
        { responseType: "blob" }
      );
      const url = window.URL.createObjectURL(
        new Blob([response.data], { type: "application/pdf" })
      );
      window.open(url, "_blank");
    } catch (e) {
      setResponse(dispatch, "ERROR", errMessage(e));
    }
  };
};

export const ResetInventoryResponse = () => {
  return async (dispatch) => {
    dispatch({
      type: inventoryConstants.RESPONSE,
      response: { state: null, message: "" },
    });
  };
};
